import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { editAdminEvent } from "@/store/slices/adminSlice";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";
import { toast } from "react-toastify";
import RouteDetailMap from "../../map/RouteDetailMap"; // Assuming this exists in components/map
import { MapPin, Navigation, Route as RouteIcon } from "lucide-react";

const EventRouteModal = ({ event, onClose }) => {
  const dispatch = useDispatch();
  const { isUpdating } = useSelector((state) => state.admin);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: event.event_route?.name || "",
    distance: event.event_route?.distance || "",
    start_point: event.event_route?.start_point || "",
    end_point: event.event_route?.end_point || "",
  });

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        event_route: {
          ...event.event_route, // keep existing data like coordinates/landmarks
          ...formData,
          distance: Number(formData.distance),
        },
      };

      await dispatch(editAdminEvent({ eventId: event._id, updates })).unwrap();
      toast.success("Route details updated");
      setIsEditing(false);
    } catch (error) {
      // Error handled by slice
    }
  };

  const rawCoordinates = event.event_route?.coordinates;
  // Handle both GeoJSON object (new) and direct array (legacy/potential)
  const coordinates = Array.isArray(rawCoordinates) 
      ? rawCoordinates 
      : rawCoordinates?.coordinates || [];

  const hasCoordinates = coordinates.length > 0;

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Route Details — ${event.title}`}
      maxWidth="max-w-4xl"
      showCloseButton={true}
    >
      <div className="space-y-6">
        {/* Map Section */}
        <div className="h-64 sm:h-80 bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 relative">
          {hasCoordinates ? (
             <RouteDetailMap 
                route={{
                    ...event.event_route,
                    coordinates: coordinates
                }} 
             />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
               <MapPin className="w-12 h-12 mb-2 opacity-50" />
               <p>No map data available</p>
            </div>
          )}
        </div>

        {/* Details Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5">
           <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                 <RouteIcon className="w-5 h-5 text-indigo-600" />
                 Route Information
              </h3>
              {!isEditing ? (
                 <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                    Edit Details
                 </Button>
              ) : (
                 <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} disabled={isUpdating}>
                       Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} loading={isUpdating}>
                       Save Changes
                    </Button>
                 </div>
              )}
           </div>

           {isEditing ? (
              <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                 <div className="sm:col-span-2">
                    <TextInput
                       label="Route Name"
                       value={formData.name}
                       onChange={(e) => setFormData({...formData, name: e.target.value})}
                       required
                    />
                 </div>
                 <TextInput
                    label="Distance (km)"
                    type="number"
                    step="0.01"
                    value={formData.distance}
                    onChange={(e) => setFormData({...formData, distance: e.target.value})}
                    required
                 />
                 <div className="hidden sm:block"></div> {/* Spacer */}
                 <TextInput
                    label="Start Point"
                    value={formData.start_point}
                    onChange={(e) => setFormData({...formData, start_point: e.target.value})}
                    required
                    icon={<Navigation className="w-4 h-4" />}
                 />
                 <TextInput
                    label="End Point"
                    value={formData.end_point}
                    onChange={(e) => setFormData({...formData, end_point: e.target.value})}
                    required
                    icon={<MapPin className="w-4 h-4" />}
                 />
              </form>
           ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                 <div>
                    <label className="text-xs text-gray-500 uppercase font-semibold">Route Name</label>
                    <p className="text-gray-900 dark:text-gray-100 font-medium">{event.event_route?.name || "—"}</p>
                 </div>
                 <div>
                    <label className="text-xs text-gray-500 uppercase font-semibold">Distance</label>
                    <p className="text-gray-900 dark:text-gray-100 font-medium">{event.event_route?.distance ? `${event.event_route.distance} km` : "—"}</p>
                 </div>
                 <div>
                    <label className="text-xs text-gray-500 uppercase font-semibold">Start Point</label>
                    <p className="text-gray-900 dark:text-gray-100 font-medium">{event.event_route?.start_point || "—"}</p>
                 </div>
                 <div>
                    <label className="text-xs text-gray-500 uppercase font-semibold">End Point</label>
                    <p className="text-gray-900 dark:text-gray-100 font-medium">{event.event_route?.end_point || "—"}</p>
                 </div>
              </div>
           )}

           {/* Landmarks Read-only for now */}
           {event.event_route?.landmarks?.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                 <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Landmarks</h4>
                 <div className="flex flex-wrap gap-2">
                    {event.event_route.landmarks.map((lm, i) => (
                       <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                          {lm.name} {lm.km_mark && `(${lm.km_mark} km)`}
                       </span>
                    ))}
                 </div>
              </div>
           )}
        </div>
      </div>
    </Modal>
  );
};

export default EventRouteModal;
